import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useEffect, useState } from "react";

import { atom, useAtom, useAtomValue } from "../app-jotai";
import { collabAPIAtom, historyChangedAtom } from "../collab/Collab";
import { getDiagramMuidFromUrl } from "../data";

// Version history for the open diagram: browse the timeline (time, author,
// label, size), save a checkpoint, and non-destructively restore any version.
export const historyPanelAtom = atom<boolean>(false);

interface Version {
  id: number;
  r2Key: string;
  author: string | null;
  label: string | null;
  elementCount: number;
  createdAt: number;
}

export const HistoryPanel = () => {
  const [open, setOpen] = useAtom(historyPanelAtom);
  const collabAPI = useAtomValue(collabAPIAtom);
  const historyNonce = useAtomValue(historyChangedAtom);
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const muid = getDiagramMuidFromUrl();

  const load = async () => {
    if (!muid) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/diagrams/${muid}/versions`);
      if (!res.ok) {
        throw new Error(`Couldn't load history (HTTP ${res.status})`);
      }
      setVersions((await res.json()) as Version[]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // reload when opened, and whenever the server signals a change
  useEffect(() => {
    if (open) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, historyNonce]);

  const saveVersion = () => {
    const label = window.prompt("Label this version (optional)") ?? undefined;
    collabAPI?.saveVersion(label || undefined);
    // the server will emit HISTORY_CHANGED; nudge a reload too
    setTimeout(load, 400);
  };

  const restore = (v: Version) => {
    if (
      window.confirm(
        "Restore this version? It's non-destructive — the current state is kept as a new version, so you can always go forward again.",
      )
    ) {
      collabAPI?.restoreVersion(v.r2Key);
      setTimeout(load, 400);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      onCloseRequest={() => setOpen(false)}
      title="Version history"
      size="small"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          paddingBottom: "1rem",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ opacity: 0.6, fontSize: 13 }}>
            {muid ? "This diagram's saved versions" : "Open a diagram first"}
          </span>
          <button
            className="excalidraw-button"
            style={{ padding: "0 1rem", fontWeight: 600 }}
            onClick={saveVersion}
            disabled={!collabAPI || !muid}
          >
            Save version
          </button>
        </div>

        {error && <div style={{ color: "var(--color-danger)" }}>{error}</div>}

        {loading ? (
          <div style={{ opacity: 0.6 }}>Loading…</div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: ".25rem",
              maxHeight: 360,
              overflowY: "auto",
            }}
          >
            {versions.length === 0 && (
              <div style={{ opacity: 0.6 }}>
                No versions yet — save one, or (in continuous mode) they appear
                as you edit.
              </div>
            )}
            {versions.map((v) => (
              <div
                key={v.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: ".5rem",
                  padding: ".4rem .6rem",
                  borderRadius: 8,
                  borderBottom: "1px solid var(--default-border-color, #eee)",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontSize: 13 }}>
                    {new Date(v.createdAt * 1000).toLocaleString()}
                    {v.label ? ` — ${v.label}` : ""}
                  </span>
                  <span style={{ fontSize: 12, opacity: 0.6 }}>
                    {v.author ?? "unknown"} · {v.elementCount} elements
                  </span>
                </div>
                <button
                  onClick={() => restore(v)}
                  disabled={!collabAPI}
                  style={{
                    background: "none",
                    border: "1px solid var(--default-border-color, #e3e2e4)",
                    borderRadius: 6,
                    padding: ".25rem .6rem",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};
